import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CheckIcon, LockClosedIcon, StarIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import SEO from '../components/SEO';
import FAQAccordion from '../components/FAQAccordion';
import { redirectToStripeCheckout } from '../lib/stripeCheckout';
import { useSession } from '@supabase/auth-helpers-react';
import toast from 'react-hot-toast';
import { trackEvent } from '../lib/analytics';
import { track } from '../lib/track';
import { getLTDTrailheadPriceId, getLTDCampfirePriceId } from '../lib/stripeCheckout';
import { supabase } from '../lib/supabase';

export default function LTDOwnersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const cancelledToastShownRef = useRef(false);

  // Check if user already has owner LTD (founding camp lifetime access)
  const { data: hasOwnerLTD, isLoading: checkingOwnerAccess } = useQuery(
    ['owner-lifetime-access', session?.user?.id],
    async () => {
      if (!session?.user?.id) return false;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('lifetime_access, subscription_plan, subscription_status')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error('Error checking owner lifetime access:', error);
        return false;
      }

      return (
        profile?.lifetime_access === true ||
        ((profile?.subscription_plan === 'ltd-trailhead' || profile?.subscription_plan === 'ltd-campfire') &&
          profile?.subscription_status === 'lifetime')
      );
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const isActuallyCheckingOwner = checkingOwnerAccess && !!session?.user?.id;

  // Handle cancel redirects (success now goes to /ltd/owners/success)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cancelled = params.get('cancelled');

    if (cancelled === 'true') {
      if (!cancelledToastShownRef.current) {
        cancelledToastShownRef.current = true;
        toast.error('Payment cancelled. You can try again when ready.', {
          duration: 5000,
          icon: '❌',
        });
        track('payment_cancelled', {
          persona: 'camp_owner',
          page: 'ltd_owners',
        });
      }
      // Clean up URL
      navigate('/ltd/owners', { replace: true });
    } else {
      cancelledToastShownRef.current = false;
    }
  }, [location.search, navigate]);

  // Remove excessive bottom padding from main element
  useEffect(() => {
    const mainElement = document.querySelector('main[role="main"]') as HTMLElement;
    if (mainElement) {
      mainElement.style.paddingBottom = '0';
      return () => {
        mainElement.style.paddingBottom = '';
      };
    }
  }, []);

  // Track page view
  useEffect(() => {
    trackEvent('ltd_owners_page_view', {
      is_authenticated: !!session,
    });
  }, [session]);

  const handleSelectPlan = async (planId: 'ltd-trailhead' | 'ltd-campfire') => {
    if (hasOwnerLTD) {
      toast.success('You already have lifetime access!', {
        duration: 3000,
        icon: '✅',
      });
      navigate('/mycamp-teaser');
      return;
    }

    trackEvent('ltd_owners_cta_clicked', {
      cta_location: 'pricing_section',
      plan: planId,
      is_authenticated: !!session,
    });
    track('ltd_owners_cta_clicked', {
      persona: 'camp_owner',
      page: 'ltd_owners',
      cta_location: 'pricing_section',
      plan: planId,
      is_authenticated: !!session,
    });

    // If not logged in, store checkout intent and redirect to registration
    if (!session) {
      try {
        sessionStorage.setItem('ltd_checkout_intent', planId);
      } catch (e) {
        console.warn('Could not set ltd_checkout_intent', e);
      }
      trackEvent('ltd_owners_checkout_blocked_no_auth', {
        plan: planId,
        redirect_to: 'register',
      });
      track('ltd_owners_checkout_blocked_no_auth', {
        persona: 'camp_owner',
        page: 'ltd_owners',
        plan: planId,
        redirect_to: 'register',
      });
      navigate(`/register?type=business&plan=${planId}`);
      return;
    }

    const priceId = planId === 'ltd-trailhead' 
      ? getLTDTrailheadPriceId()
      : getLTDCampfirePriceId();

    if (!priceId) {
      toast.error('Payment system not configured. Please contact support.', {
        duration: 5000,
      });
      return;
    }

    try {
      trackEvent('ltd_owners_checkout_initiated', {
        plan: planId,
        is_authenticated: true,
      });
      track('ltd_owners_checkout_initiated', {
        persona: 'camp_owner',
        page: 'ltd_owners',
        plan: planId,
        is_authenticated: true,
      });

      await redirectToStripeCheckout({
        priceId,
        planName: planId,
        userEmail: session?.user?.email,
      });
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      trackEvent('ltd_owners_checkout_error', {
        plan: planId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      track('ltd_owners_checkout_error', {
        persona: 'camp_owner',
        page: 'ltd_owners',
        plan: planId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to start checkout. Please try again.',
        { duration: 5000 }
      );
    }
  };

  const faqs = [
    {
      '@type': 'Question' as const,
      name: 'Will this become subscription later?',
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: 'Yes — for new camps.',
      },
    },
    {
      '@type': 'Question' as const,
      name: 'Will I ever be asked to pay again?',
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: 'No. Founding access is permanent.',
      },
    },
    {
      '@type': 'Question' as const,
      name: 'Does this affect search ranking?',
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: 'Yes — higher tiers get better placement.',
      },
    },
  ];

  return (
    <div className="page-container" style={{ minHeight: 'auto', paddingBottom: 0 }}>
      <SEO
        title="CampWhiz for Camp Owners - Lifetime Deal | Founding Camps"
        description="Get discovered by local parents — before CampWhiz gets crowded. Claim your camp profile and lock in lifetime visibility. Pay once. No subscriptions."
        keywords="camp owner, lifetime deal, camp listing, camp visibility, campwhiz owners"
        url="/ltd/owners"
      />

      <div className="page-content">
        {/* Hero Section */}
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Get discovered by local parents — before CampWhiz gets crowded.
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 mb-8 leading-relaxed">
            Claim your camp profile and lock in lifetime visibility.
          </p>
          <p className="text-lg text-gray-600 mb-8">
            Pay once. No subscriptions.
          </p>
        </div>

        {/* Social Proof / Context */}
        <div className="max-w-3xl mx-auto mb-16 text-center">
          <p className="text-lg text-gray-700 mb-4">
            CampWhiz is growing as a parent-first discovery platform.
          </p>
          <p className="text-lg text-gray-700 font-semibold">
            Early camps get permanent advantages.
          </p>
        </div>

        {/* Problem Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Parents want:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
              <p className="text-lg text-gray-800">Clear info</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
              <p className="text-lg text-gray-800">Real photos</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
              <p className="text-lg text-gray-800">Confidence in their choice</p>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            But most camps:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center">
              <p className="text-lg text-gray-800">Rely on outdated directories</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center">
              <p className="text-lg text-gray-800">Compete blindly for attention</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center">
              <p className="text-lg text-gray-800">Re-pay visibility fees every year</p>
            </div>
          </div>
        </div>

        {/* Opportunity Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Early camps win long-term.
          </h2>
          <div className="bg-primary/10 rounded-xl p-8 border border-primary/20">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Founding camps:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <CheckIcon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-800">Rank higher</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckIcon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-800">Stand out visually</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckIcon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-800">Lock in access before subscriptions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Founding Camps Lifetime Access
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Trailhead */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 relative">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Trailhead — Lifetime</h3>
              <p className="text-gray-600 mb-6">Own your listing. Control your story.</p>
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-2">
                  <div className="text-2xl line-through text-gray-400">$149 / year</div>
                  <div className="text-4xl font-bold text-primary">$149 one-time</div>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Edit description, hours, photos</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Control how parents see your camp</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Lifetime profile ownership</span>
                </li>
              </ul>
              {hasOwnerLTD ? (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-center gap-2 text-green-800 font-semibold mb-2">
                    <CheckIcon className="h-5 w-5" />
                    You have lifetime access
                  </div>
                  <Link
                    to="/mycamp-teaser"
                    className="block w-full text-center btn btn-secondary"
                  >
                    Go to My Camp
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => handleSelectPlan('ltd-trailhead')}
                  className="w-full btn btn-secondary"
                  disabled={isActuallyCheckingOwner}
                >
                  {isActuallyCheckingOwner ? 'Checking...' : 'Claim Lifetime Access'}
                </button>
              )}
            </div>

            {/* Campfire - Most Popular */}
            <div className="bg-primary rounded-2xl shadow-lg border-2 border-primary p-8 relative text-white">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <StarIcon className="h-4 w-4" />
                  Most Popular
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-2">Campfire — Lifetime</h3>
              <p className="text-white/90 mb-6">Be seen first by local parents.</p>
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-2">
                  <div className="text-2xl line-through text-white/60">$249 / year</div>
                  <div className="text-4xl font-bold">$249 one-time</div>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                  <span>Everything in Trailhead</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                  <span>Higher placement in local results</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                  <span>Featured Camp badge</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                  <span>Priority visibility as traffic grows</span>
                </li>
              </ul>
              {hasOwnerLTD ? (
                <div className="bg-white/20 border-2 border-white rounded-lg px-4 py-3">
                  <div className="flex items-center justify-center gap-2 font-semibold mb-2">
                    <CheckIcon className="h-5 w-5" />
                    You have lifetime access
                  </div>
                  <Link
                    to="/mycamp-teaser"
                    className="block w-full text-center btn btn-secondary hover:bg-white hover:text-primary"
                  >
                    Go to My Camp
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => handleSelectPlan('ltd-campfire')}
                  className="w-full btn btn-secondary hover:bg-white hover:text-primary"
                  disabled={isActuallyCheckingOwner}
                >
                  {isActuallyCheckingOwner ? 'Checking...' : 'Claim Lifetime Access'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Why Lifetime Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Why are you offering this?
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="text-lg">
                We're building CampWhiz with early partners.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Lifetime access rewards early trust</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Subscriptions come later</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Founding camps keep their advantages</span>
                </li>
              </ul>
              <p className="text-lg font-semibold mt-4">
                This offer will close once regions fill.
              </p>
            </div>
          </div>
        </div>

        {/* Who This Is For / Not For */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Great fit if:</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">You run local or regional camps</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Parents already ask "where can I find you?"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">You want durable visibility</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Not a fit if:</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-gray-700">You don't want to manage your listing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-700">You only rely on word of mouth</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <FAQAccordion faqs={faqs} />
        </div>

        {/* Final CTA */}
        <div className="max-w-4xl mx-auto mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Lock in lifetime access while it's available.
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Pay once. Benefit forever.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {hasOwnerLTD ? (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg px-6 py-4 inline-flex flex-col sm:flex-row items-center justify-center gap-3">
                <div className="flex items-center gap-2 text-green-800 font-semibold">
                  <CheckIcon className="h-5 w-5" />
                  You have lifetime access
                </div>
                <Link
                  to="/mycamp-teaser"
                  className="btn btn-primary btn-lg px-8 py-4"
                >
                  Go to My Camp
                </Link>
              </div>
            ) : (
              <>
                <button
                  onClick={() => handleSelectPlan('ltd-trailhead')}
                  className="btn btn-secondary btn-lg px-8 py-4"
                  disabled={isActuallyCheckingOwner}
                >
                  {isActuallyCheckingOwner ? 'Checking...' : 'Claim Trailhead'}
                </button>
                <button
                  onClick={() => handleSelectPlan('ltd-campfire')}
                  className="btn btn-primary btn-lg px-8 py-4"
                  disabled={isActuallyCheckingOwner}
                >
                  {isActuallyCheckingOwner ? 'Checking...' : 'Claim Campfire'}
                </button>
              </>
            )}
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
            <LockClosedIcon className="h-4 w-4" />
            <span>Secure checkout by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  );
}

